/**
 * MockedSellerMap — an inline-SVG stand-in for the (un-integrated) 2GIS map.
 *
 * 2GIS is not wired for the demo, so this is a styled mock: a decorative grid +
 * "roads" backdrop with a single pin projected from the seller's real `lat/lng`
 * relative to the market center, a "Карты · 2GIS" attribution, and Call /
 * Directions / WhatsApp CTAs. The real `SellerMap.tsx` (2GIS MapGL) stays
 * unused.
 *
 * CSP-strict: inline SVG only, no `<script>`, no `dangerouslySetInnerHTML`.
 * Accessibility (ui-ux-pro-max): the SVG is `role="img"` with a descriptive
 * `aria-label`; the decorative grid is `aria-hidden`; the pin carries a
 * `<title>`; CTAs are real `<a>` elements with labels and ≥44px hit areas.
 * Tokens via the `--g-*` mirror; strings from the `marketplace` namespace.
 */
import { MapPin, MessageCircle, Navigation, Phone } from "lucide-react";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type {
  ProductDetailMarket,
  ProductDetailSeller,
} from "@/features/marketplace/useProductDetail";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Project a seller's (lat,lng) into the 0..100 SVG viewBox relative to the
 * market center. Small deterministic spans give a visible spread for the
 * seeded ±~0.0012° scatter; null coords fall to the center.
 */
function projectPin(
  seller: ProductDetailSeller,
  market: ProductDetailMarket,
): { x: number; y: number } {
  if (seller.lat === null || seller.lng === null) {
    return { x: 50, y: 50 };
  }
  // Coordinates arrive as Decimal-serialized strings; coerce + guard.
  const sLat = Number(seller.lat);
  const sLng = Number(seller.lng);
  const mLat = Number(market.lat);
  const mLng = Number(market.lng);
  if (
    !Number.isFinite(sLat) ||
    !Number.isFinite(sLng) ||
    !Number.isFinite(mLat) ||
    !Number.isFinite(mLng)
  ) {
    return { x: 50, y: 50 };
  }
  const dx = sLng - mLng;
  const dy = sLat - mLat;
  const x = clamp(50 + (dx / 0.002) * 40, 8, 92);
  // Higher latitude → further north → smaller y (up) in the viewBox.
  const y = clamp(50 - (dy / 0.0015) * 40, 8, 92);
  return { x, y };
}

/** Strip a leading "+" so a phone forms a valid wa.me path. */
function waNumber(e164: string): string {
  return e164.replace(/[^\d]/g, "");
}

export function MockedSellerMap({
  seller,
  market,
}: {
  seller: ProductDetailSeller;
  market: ProductDetailMarket;
}): ReactElement {
  const { t } = useTranslation("marketplace");
  const pin = projectPin(seller, market);
  const directionsHref = `https://2gis.kg/search/${encodeURIComponent(
    market.name_ru,
  )}`;
  const ctaClass =
    "inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-[12px] border border-border px-3 text-sm font-medium transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

  return (
    <div data-testid="mocked-seller-map" className="flex flex-col gap-3">
      <div className="relative overflow-hidden rounded-[14px] border border-border">
        <svg
          viewBox="0 0 100 100"
          role="img"
          aria-label={t("map.regionLabel", {
            seller: seller.business_name,
            market: market.name_ru,
            pavilion: seller.pavilion_number,
          })}
          className="h-56 w-full bg-[color:var(--g-primary-soft)]"
          preserveAspectRatio="xMidYMid slice"
        >
          <g aria-hidden="true" stroke="var(--g-hairline)" strokeWidth={0.4}>
            {/* Decorative grid */}
            {[15, 30, 45, 60, 75, 90].map((p) => (
              <line key={`h-${p}`} x1={0} y1={p} x2={100} y2={p} />
            ))}
            {[15, 30, 45, 60, 75, 90].map((p) => (
              <line key={`v-${p}`} x1={p} y1={0} x2={p} y2={100} />
            ))}
            {/* Decorative "roads" */}
            <line
              x1={0}
              y1={62}
              x2={100}
              y2={38}
              stroke="var(--g-hairline)"
              strokeWidth={1.6}
            />
            <line
              x1={28}
              y1={0}
              x2={64}
              y2={100}
              stroke="var(--g-hairline)"
              strokeWidth={1.6}
            />
          </g>
          {/* Pin (real projected position). A foreignObject would need HTML;
              an SVG marker keeps it CSP-safe and crisp. */}
          <g transform={`translate(${pin.x}, ${pin.y})`}>
            <title>
              {t("map.pinTitle", {
                seller: seller.business_name,
                pavilion: seller.pavilion_number,
              })}
            </title>
            <circle cx={0} cy={0} r={6} fill="var(--g-primary)" opacity={0.18} />
            <circle
              cx={0}
              cy={0}
              r={2.6}
              fill="var(--g-primary)"
              stroke="var(--g-surface, #fff)"
              strokeWidth={0.8}
            />
          </g>
        </svg>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-1 left-2 text-[10px] text-muted-foreground"
        >
          {t("map.attribution")}
        </span>
        {/* Decorative zoom buttons (non-functional mock). */}
        <div
          aria-hidden="true"
          className="absolute right-2 top-2 flex flex-col overflow-hidden rounded-[8px] border border-border bg-background/80 text-sm"
        >
          <span className="flex size-7 items-center justify-center">+</span>
          <span className="flex size-7 items-center justify-center border-t border-border">
            −
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <a
          href={`tel:${seller.phone_e164}`}
          className={ctaClass + " bg-primary text-primary-foreground border-primary"}
          data-testid="map-cta-call"
        >
          <Phone className="size-4" aria-hidden="true" />
          {t("map.call")}
        </a>
        <a
          href={directionsHref}
          target="_blank"
          rel="noopener noreferrer"
          className={ctaClass}
          data-testid="map-cta-directions"
        >
          <Navigation className="size-4" aria-hidden="true" />
          {t("map.directions")}
        </a>
        {seller.whatsapp_phone_e164 !== null ? (
          <a
            href={`https://wa.me/${waNumber(seller.whatsapp_phone_e164)}`}
            target="_blank"
            rel="noopener noreferrer"
            className={ctaClass}
            data-testid="map-cta-whatsapp"
          >
            <MessageCircle className="size-4" aria-hidden="true" />
            {t("map.whatsapp")}
          </a>
        ) : null}
      </div>

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <MapPin className="size-3.5" aria-hidden="true" />
        {market.name_ru} · {market.address}
      </p>
    </div>
  );
}

export default MockedSellerMap;
